import type { SubmitFeedbackRequest } from '../types/feedback';
import type { ChatServiceConfiguration } from './chatService';

const DEFAULT_HEADERS = {
    'Content-Type': 'application/json'
} as const;

export class FeedbackService
{
    private readonly endpoint: string;
    private readonly headers: Record<string, string>;
    private readonly getAuthorizationToken?: () => string | Promise<string>;

    public constructor(configuration: ChatServiceConfiguration)
    {
        this.endpoint = resolveFeedbackEndpoint(configuration);
        this.headers = normalizeHeaders(configuration);
        this.getAuthorizationToken = configuration.getAuthorizationToken;
    }

    public async submit(request: SubmitFeedbackRequest, threadId?: string | null): Promise<void>
    {
        validateRequest(request);

        const headers: Record<string, string> = {
            ...DEFAULT_HEADERS,
            ...this.headers
        };

        const authorizationToken = await resolveAuthorizationHeader(this.getAuthorizationToken);
        if (authorizationToken)
        {
            headers.Authorization = authorizationToken;
        }

        if (threadId?.trim())
        {
            headers['thread-id'] = threadId.trim();
        }

        const response = await fetch(this.endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(request)
        });

        if (!response.ok)
        {
            const errorText = await safeReadText(response);
            throw new Error(errorText || `Feedback request failed with status ${response.status}.`);
        }
    }
}

function resolveFeedbackEndpoint(configuration: ChatServiceConfiguration): string
{
    if (configuration.feedbackEndpoint?.trim())
    {
        return configuration.feedbackEndpoint.trim();
    }

    const chatUrl = new URL(configuration.endpoint);
    const path = chatUrl.pathname;
    const basePath = path.endsWith('/chat') ? path.slice(0, -5) : path;
    const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;

    chatUrl.pathname = `${normalizedBasePath}/feedback`;
    return chatUrl.toString();
}

function normalizeHeaders(configuration: ChatServiceConfiguration): Record<string, string>
{
    const normalized: Record<string, string> = {};

    Object.entries(configuration.headers ?? {}).forEach(([key, value]) =>
    {
        if (!value)
        {
            return;
        }

        if (key.trim().toLowerCase() === 'authorization')
        {
            return;
        }

        normalized[key] = value;
    });

    return normalized;
}

function validateRequest(request: SubmitFeedbackRequest): void
{
    if (!request.conversationId?.trim())
    {
        throw new Error('conversationId is required.');
    }

    if (!request.clientEventId?.trim())
    {
        throw new Error('clientEventId is required.');
    }

    if (request.type === 'negative')
    {
        if (!request.reason)
        {
            throw new Error('reason is required for negative feedback.');
        }

        if ((request.comment?.trim().length ?? 0) > 2000)
        {
            throw new Error('comment cannot exceed 2000 characters.');
        }

        return;
    }

    if (request.reason || request.comment?.trim())
    {
        throw new Error('reason/comment are not allowed for positive feedback.');
    }
}

async function safeReadText(response: Response): Promise<string>
{
    try
    {
        return await response.text();
    }
    catch
    {
        return '';
    }
}

async function resolveAuthorizationHeader(
    getAuthorizationToken?: () => string | Promise<string>
): Promise<string | undefined>
{
    if (!getAuthorizationToken)
    {
        return undefined;
    }

    try
    {
        const token = await getAuthorizationToken();
        const normalizedToken = normalizeAuthorizationToken(token);
        return normalizedToken;
    }
    catch
    {
        return undefined;
    }
}

function normalizeAuthorizationToken(token: string): string | undefined
{
    const trimmedToken = token?.trim();
    if (!trimmedToken)
    {
        return undefined;
    }

    return trimmedToken.startsWith('Bearer ') ? trimmedToken : `Bearer ${trimmedToken}`;
}
